import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  effect,
  EventEmitter,
  Input,
  input,
  OnInit,
  Output,
  signal,
  WritableSignal
} from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { ComponentType, InputType, PromptComponent, PromptInput } from '@k12/models';
import { toBool } from '@k12/utils';

@Component({
  selector: 'lib-pc-checkbox',
  imports: [CommonModule, MatButtonModule, MatCheckboxModule, ReactiveFormsModule],
  templateUrl: './pc-checkbox.component.html',
  styleUrl: './pc-checkbox.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class PcCheckboxComponent implements OnInit {
  @Input() allowPointerEvents!: boolean;
  @Input() configJson: PromptComponent = {
    componentType: ComponentType.CheckboxFormField,
    header: '',
    inputType: InputType.Checkbox,
    instructions: '',
    label: '',
    order: 0,
    inputs: [
      {
        dataType: InputType.Checkbox,
        inputType: InputType.Checkbox,
        instructions: '',
        isRequired: false,
        label: 'Add descriptive text for this check box.',
        order: 0,
        options: [],
        link: {}
      }
    ]
  };
  @Input() formGroup!: FormGroup;

  @Output() cbConfig = new EventEmitter();
  @Output() checkboxSelect = new EventEmitter();
  @Output() openDrawer = new EventEmitter();

  public ComponentType: typeof ComponentType = ComponentType;
  public displayAdminFormField: WritableSignal<boolean> = signal(false);
  public editFormFieldInput = input<boolean>();
  public enableAddTextInputButton = input<boolean>();
  public enrollmentBuilderView = input<boolean>();
  public isEditDrawerOpen: WritableSignal<boolean> = signal(false);
  public showAddCheckboxButton: WritableSignal<boolean> = signal(false);

  private localTrackIdSeq = 0;

  constructor() {
    effect(() => {
      if (this.editFormFieldInput()) {
        this.showAddCheckboxButton.set(true);
      }
    });
  }

  public ngOnInit(): void {
    if (this.enrollmentBuilderView() && this.configJson.inputs?.length !== 0) {
      this.displayAdminFormField.set(true);
    }
    this.cbConfig.emit(this.configJson);
    if (!this.formGroup) {
      this.formGroup = new FormGroup({});
    }
    this.configJson.inputs.forEach(item => {
      this.assignLocalTrackId(item);
      const name = this.controlName(item);
      if (!this.formGroup.contains(name)) {
        this.formGroup.addControl(name, new FormControl(false));
      }
    });

    // Todo comment to move the method into the returnFormInputValue when we can
    this.normalizeFormBooleans(this.formGroup);
  }

  // Saved inputs are keyed by their server-assigned GUID; unsaved (admin builder) inputs use a
  // transient local control name that is never sent to the server.
  public controlName(input: PromptInput): string {
    return input.id ?? input.localTrackId ?? `local_${input.order ?? 0}`;
  }

  public addInputField(): void {
    const newInput = {
      inputType: InputType.Checkbox,
      instructions: '',
      order: this.configJson.inputs.length,
      label: 'Add descriptive text for this check box.',
      dataType: 'boolean',
      isRequired: false,
      link: {},
      options: [],
      localTrackId: this.nextLocalTrackId()
    };
    this.configJson = {
      ...this.configJson,
      inputs: [...this.configJson.inputs, newInput]
    };
    this.rebuildControls();
    this.cbConfig.emit(this.configJson);
  }


  public checkboxClick(checked: boolean, controlName: string): void {
    this.checkboxSelect.emit({
      formName: this.configJson.id ?? '',
      control: { [controlName]: checked }
    });
  }

  public openEditDrawer(index: number): void {
    this.isEditDrawerOpen.set(true);
    this.openDrawer.emit({ openDrawer: true, editIndex: index });
  }

  public removeField(index: number): void {
    const updatedInputs = this.configJson.inputs
      .filter((_, i) => i !== index)
      .map((item, i) => ({
        ...item,
        order: i
      }));

    this.configJson = {
      ...this.configJson,
      inputs: updatedInputs
    };

    this.rebuildControls();
    this.cbConfig.emit(this.configJson);
  }

  // Add/remove only happen in the admin builder where the control values are throwaway, so
  // the group can simply be rebuilt to match the current inputs.
  private rebuildControls(): void {
    if (!this.formGroup) return;
    Object.keys(this.formGroup.controls).forEach((name) => this.formGroup.removeControl(name, { emitEvent: false }));
    this.configJson.inputs.forEach((input) => {
      this.formGroup.addControl(this.controlName(input), new FormControl(false), { emitEvent: false });
    });
  }

  private normalizeFormBooleans(form: FormGroup): void {
    Object.keys(form.controls).forEach(key => {
      const control = form.get(key);
      if (!control) return;
      const val = control.value;
      // if it's string (or empty) convert to boolean
      if (typeof val === 'string' || val === '' || typeof val === 'number') {
        control.setValue(toBool(val), { emitEvent: false });
      }
    });
  }

  // Gives unsaved inputs (no server-assigned id) a stable identity, set once at creation, so
  // the template can track them by plain property access instead of calling a method on every
  // change-detection pass. Stripped before the config is persisted -- see PromptInput.localTrackId.
  private assignLocalTrackId(input: PromptInput): void {
    if (!input.id && !input.localTrackId) {
      input.localTrackId = this.nextLocalTrackId();
    }
  }

  private nextLocalTrackId(): string {
    return `local_${this.localTrackIdSeq++}`;
  }
}
